
import { useQuery } from "react-query";
import { useSelector, useDispatch } from "react-redux";
import { selectSelectedSchoolId } from "app/modules/schools/selectors";
import {
  selectClassList,
  selectClassDataLastFetched,
  selectClassDataLoading,
  fetchClassData,
  clearClassList,
} from "app/services/classDataSlice";

const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours

export const useClassData = (options = {}) => {
  const dispatch = useDispatch();
  const selectedSchoolId = useSelector(selectSelectedSchoolId);
  const classList = useSelector(selectClassList);
  const lastFetched = useSelector(selectClassDataLastFetched);
  const isReduxLoading = useSelector(selectClassDataLoading);

  // Check if we need to fetch fresh data
  const shouldFetch =
    !lastFetched ||
    Date.now() - lastFetched > CACHE_DURATION ||
    classList.length === 0;

  const {
    isFetched,
    isLoading: isQueryLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["classDropdown", selectedSchoolId],
    queryFn: async () => {
      // Use Redux thunk instead of direct API call
      const result = await dispatch(fetchClassData(selectedSchoolId));
      return result.payload;
    },
    enabled: shouldFetch && !!selectedSchoolId,
    staleTime: CACHE_DURATION,
    cacheTime: CACHE_DURATION,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: 2,
    onError: (error) => {
      console.error("Failed to fetch class data:", error);
    },
    ...options,
  });

  const isLoading = isQueryLoading || isReduxLoading;

  return {
    isFetched,
    classList,
    isLoading,
    error,
    refetch: () => {
      dispatch(clearClassList());
      return refetch();
    },
    lastFetched: lastFetched ? new Date(lastFetched) : null,
  };
};
